import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TextareaAutosize from 'react-textarea-autosize';

import {
  IconMultiCheckBlueCircle,
  IconMultiCheckGreyCircle,
  IconMultiClipboardBlue,
  IconMultiCloudLogo,
  IllustrationTrashcanCircle,
} from '@snorose/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  ActionButton,
  AttachmentList,
  Badge,
  CheckBox,
  CloseAppBar,
  ConfirmModal,
  DropdownCategory,
  FetchLoading,
} from '@/shared/component';
import {
  ATTACHMENT_MODAL_TEXT,
  BOARD_CATEGORY_MAP,
  BOARD_MENUS,
  CONFIRM_MODAL_TEXT,
  QUERY_KEY,
  ROLE,
  TOAST,
} from '@/shared/constant';
import { ModalContext } from '@/shared/context/ModalContext';
import {
  useAuth,
  useBlocker,
  useBoard,
  useBoardNavigate,
  useModal,
  useToast,
} from '@/shared/hook';
import { DateTime, getBoard } from '@/shared/lib';

import { Guideline } from '@/feature/attachment/component';
import { useGuide } from '@/feature/attachment/hook';
import { AttachmentBar } from '@/feature/board/component';
import { EditorContainer } from '@/feature/editor/component';
import { preserveEmptyParagraphs } from '@/feature/editor/lib/emptyFormat';
import { sanitizeHtml } from '@/feature/editor/lib/sanitize';

import attachmentGuide1 from '@/assets/images/attachmentGuide1.png';
import attachmentGuide2 from '@/assets/images/attachmentGuide2.png';

import { createThumbnail, postPost } from '@/apis';

import styles from './WritePostPage.module.css';

export default function WritePostPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const { toast } = useToast();
  const { userInfo } = useAuth();
  const { invalidUserInfoQuery } = useAuth();
  const { modal, setModal } = useContext(ModalContext);

  const [isNotice, setIsNotice] = useState(false);
  const [category, setCategory] = useState(null);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const submitLockRef = useRef(false);
  const [isBlock, setIsBlock] = useState(false);
  //가이드 이미지 관련 로직
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const { isGuideOpened, closeGuide, disableGuide } = useGuide({
    guideKey: 'attachmentGuide',
    maxGuideVisitNum: 3,
  });
  //'게시글 생성' API에서 요구하는 데이터 (중 attachments array)
  const [attachmentsInfo, setAttachmentsInfo] = useState([]);

  const [isTrashOverlapped, setIsTrashOverlapped] = useState(false);
  const [trashImageIndex, setTrashImageIndex] = useState(null); //지우는 이미지 index
  const trashImageConfirmModal = useModal();

  const textId = pathname.split('/')[2];
  const currentBoard = getBoard(textId);
  const [isTitleFocused, setIsTitleFocused] = useState(false);
  const boardTitle = currentBoard?.title;
  const boardId = currentBoard?.id ?? '';
  const categoryConfig = BOARD_CATEGORY_MAP[boardId];
  const hasCategory = Boolean(categoryConfig);
  const isCategoryDisabled = hasCategory && isNotice;
  const shouldIncludeCategory = hasCategory && !isCategoryDisabled;

  // 페이지 이탈 방지 모달 노출
  useEffect(() => {
    setIsBlock(
      title.trim().length > 0 ||
        text.trim().length > 0 ||
        attachmentsInfo.length > 0
    );
  }, [title, text, attachmentsInfo]);

  useBlocker(isBlock);

  // 게시글 작성 중 페이지 이탈
  const handleExitPage = () => {
    setModal({
      id: null,
      type: null,
    });
    setIsBlock(false);
    navigate(-1);
  };

  // 공지 여부 선택 핸들러
  const handleIsNotice = () => {
    const willBeNotice = !isNotice;

    if (hasCategory && willBeNotice) {
      setCategory(null);
    }

    setIsNotice(willBeNotice);
  };

  const data = {
    category: shouldIncludeCategory ? category : '',
    boardId,
    title,
    content: text,
    isNotice: textId === 'notice' ? true : isNotice,
    attachmentsInfo: attachmentsInfo,
  };

  const createThumbnailMutation = useMutation({
    mutationFn: ({ boardId, postId }) => createThumbnail(boardId, postId),
  });
  const createPostMutation = useMutation({
    mutationFn: postPost,
    onSuccess: async (response, variables) => {
      if (response.status !== 201) return;

      !response.data.result.pointDifference
        ? toast.success(TOAST.POST.createNoPoints)
        : toast.success(TOAST.POST.create);

      const newPostId = response.data.result.postId;
      try {
        await createThumbnailMutation.mutateAsync({
          boardId: variables.boardId,
          postId: newPostId,
        });
      } catch (err) {
        toast.error('썸네일 생성 중 오류가 발생했습니다.');
      }

      queryClient.removeQueries(QUERY_KEY.post());
      invalidUserInfoQuery();

      currentBoard.id === 12 || variables.isNotice
        ? navigate(`/board/${currentBoard.textId}/notice`, { replace: true })
        : navigate(
            `/board/${
              BOARD_MENUS.find((menu) => menu.id === variables.boardId).textId
            }/post/${newPostId}`,
            { replace: true }
          );
    },
    onError: (err) => {
      toast.error(err.response?.data?.message);
    },
  });
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!boardId) {
      toast.info(TOAST.POST.selectBoard);
      return;
    }

    if (shouldIncludeCategory && !category) {
      toast.info(TOAST.POST.selectCategory);
      return;
    }

    if (!title.trim()) {
      toast.info(TOAST.POST.emptyTitle);
      return;
    }
    if (!text.trim()) {
      toast.info(TOAST.POST.emptyContent);
      return;
    }

    if (submitLockRef.current) return;
    submitLockRef.current = true;

    createPostMutation.mutate(data, {
      onSettled: () => {
        submitLockRef.current = false;
      },
    });
  };

  // 제목 127자 제한
  const handleTitleChange = (e) => {
    const newValue = e.target.value;
    if (newValue.length <= 127) {
      setTitle(newValue);
    }
  };

  //에디터 컴포넌트
  const [editor, setEditor] = useState(null);

  return (
    <>
      {isGuideOpened && (
        <Guideline
          guideImages={[attachmentGuide1, attachmentGuide2]}
          guideStyle={{
            width: '27.4rem',
            height: '36.1rem',
            position: 'absolute',
            bottom: '10.2rem',
          }}
        >
          <div className={styles.buttons}>
            <label className={styles.guideButton1}>
              <CheckBox
                id='dontShowAgain'
                checked={dontShowAgain}
                onChange={() => setDontShowAgain((prev) => !prev)}
              />
              <p className={styles.guideButton1Text}>다시 보지 않기</p>
            </label>
            <button
              className={styles.guideButton2}
              onClick={() => {
                if (dontShowAgain) {
                  disableGuide();
                }
                closeGuide();
              }}
            >
              닫기
            </button>
          </div>
        </Guideline>
      )}
      <div className={styles.container}>
        {(createPostMutation.isPending ||
          createThumbnailMutation.isPending) && (
          <div className={styles.fetchLoadingContainer}>
            <FetchLoading>게시글 처리 중...</FetchLoading>
          </div>
        )}
        <div className={styles.main}>
          <div className={styles.top}>
            <CloseAppBar backgroundColor={'#fbfdff'}>
              <ActionButton
                onClick={handleSubmit}
                disabled={
                  createPostMutation.isPending ||
                  createThumbnailMutation.isPending
                }
              >
                등록
              </ActionButton>
            </CloseAppBar>
          </div>
          <div className={styles.center}>
            <div className={styles.categorySelect}>
              <div className={styles.categorySelectContainer}>
                <IconMultiClipboardBlue />
                <p className={styles.categorySelectText}>{boardTitle}</p>
              </div>
            </div>
            {categoryConfig && !isCategoryDisabled && (
              <DropdownCategory
                options={categoryConfig}
                value={category}
                onChange={setCategory}
              />
            )}
            <div className={styles.profileBox}>
              <div className={styles.profileBoxLeft}>
                {userInfo?.userRoleId !== ROLE.admin &&
                userInfo?.userRoleId !== ROLE.official ? (
                  <IconMultiCloudLogo
                    width={25}
                    height={16}
                    role='img'
                    aria-label='로고'
                  />
                ) : (
                  <Badge
                    userRoleId={userInfo?.userRoleId}
                    width={24}
                    height={24}
                  />
                )}
                <p>{userInfo?.nickname}</p>
                <p className={styles.dot}></p>
                <p>{DateTime.format(new Date(), 'MD_HM')}</p>
              </div>
              {textId !== 'notice' && (
                <div
                  className={
                    userInfo?.userRoleId === ROLE.admin ||
                    userInfo?.userRoleId === ROLE.official
                      ? styles.profileBoxRight
                      : styles.profileBoxRightInvisible
                  }
                  onClick={handleIsNotice}
                >
                  {isNotice ? (
                    <IconMultiCheckBlueCircle width={21} height={22} />
                  ) : (
                    <IconMultiCheckGreyCircle width={21} height={22} />
                  )}
                  <p>공지글</p>
                </div>
              )}
            </div>
            <div className={styles.content}>
              <TextareaAutosize
                className={styles.title}
                placeholder='제목을 입력해주세요'
                value={title}
                onChange={handleTitleChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                  }
                }}
                onFocus={() => setIsTitleFocused(true)}
                onBlur={() => setIsTitleFocused(false)}
              />
              {/*<TextareaAutosize
                  className={styles.text}
                  placeholder='내용'
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                />*/}
              <EditorContainer
                placeholder='내용'
                onEditorReady={(editorInstance) => setEditor(editorInstance)}
                onChangeEditor={(editor) => {
                  const sanitized = sanitizeHtml(
                    preserveEmptyParagraphs(editor.getHTML())
                  );
                  setText(sanitized);
                }}
              />
              <AttachmentList
                attachmentsInfo={attachmentsInfo}
                setAttachmentsInfo={setAttachmentsInfo}
              />
            </div>
          </div>
        </div>

        {modal.id === 'exit-page' && (
          <ConfirmModal
            modalText={CONFIRM_MODAL_TEXT.EXIT_PAGE}
            onConfirm={handleExitPage}
          />
        )}

        <IllustrationTrashcanCircle
          width={21}
          height={22}
          className={`${isTrashOverlapped ? styles.trashVisible : styles.trashInvisible}`}
          onDragEnter={(e) => {
            e.preventDefault();
            setIsTrashOverlapped(true);
          }}
          onDragOver={(e) => {
            setIsTrashOverlapped(true);
            e.preventDefault();
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsTrashOverlapped(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            const draggedIndex = parseInt(
              e.dataTransfer.getData('text/plain'),
              10
            );
            setTrashImageIndex(draggedIndex);
            setIsTrashOverlapped(false);
            trashImageConfirmModal.openModal();
          }}
        />
        <AttachmentBar
          attachmentsInfo={attachmentsInfo}
          setAttachmentsInfo={setAttachmentsInfo}
          editor={editor}
          isTitleFocused={isTitleFocused}
        />
      </div>

      {trashImageConfirmModal.isOpen && (
        <ConfirmModal
          modalText={ATTACHMENT_MODAL_TEXT.DELETE_ATTACHMENT}
          onConfirm={() => {
            setAttachmentsInfo((prev) =>
              prev
                .slice(0, trashImageIndex)
                .concat(prev.slice(trashImageIndex + 1))
            );
            trashImageConfirmModal.closeModal();
          }}
          onCancel={() => {
            trashImageConfirmModal.closeModal();
          }}
        />
      )}
    </>
  );
}

/**
 * TODO: 라우트 개선 작업 완료 후 기존 컴포넌트와 교체 예정
 */
export function NewWritePostPage({ isNotice = false }) {
  const navigate = useNavigate();
  const {
    key: boardKey,
    id: boardId,
    name: boardName,
    isGlobalNotice,
  } = useBoard();
  const { toNoticeDetail, toDetail } = useBoardNavigate();

  const queryClient = useQueryClient();

  const submitLockRef = useRef(false);
  const { userInfo, invalidUserInfoQuery } = useAuth();
  const { toast } = useToast();
  const { modal, setModal } = useContext(ModalContext);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isTitleFocused, setIsTitleFocused] = useState(false);
  const [editor, setEditor] = useState(null);

  const [isBlock, setIsBlock] = useState(false);

  //가이드 이미지 관련 로직
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const { isGuideOpened, closeGuide, disableGuide } = useGuide({
    guideKey: 'attachmentGuide',
    maxGuideVisitNum: 3,
  });

  //'게시글 생성' API에서 요구하는 데이터 (중 attachments array)
  const [attachmentsInfo, setAttachmentsInfo] = useState([]);

  const [isTrashOverlapped, setIsTrashOverlapped] = useState(false);
  const [trashImageIndex, setTrashImageIndex] = useState(null); //지우는 이미지 index
  const trashImageConfirmModal = useModal();

  // 페이지 이탈 방지 모달 노출
  useEffect(() => {
    setIsBlock(
      title.trim().length > 0 ||
        content.trim().length > 0 ||
        attachmentsInfo.length > 0
    );
  }, [title, content, attachmentsInfo]);

  useBlocker(isBlock);

  // 게시글 작성 중 페이지 이탈
  const handleExitPage = () => {
    setModal({
      id: null,
      type: null,
    });
    setIsBlock(false);
    navigate(-1);
  };

  // 제목 127자 제한
  const handleTitleChange = (e) => {
    const newValue = e.target.value;
    if (newValue.length <= 127) {
      setTitle(newValue);
    }
  };

  const data = {
    category: null,
    boardId,
    title,
    content,
    isNotice,
    attachmentsInfo: attachmentsInfo,
  };

  const createThumbnailMutation = useMutation({
    mutationFn: ({ boardId, postId }) => createThumbnail(boardId, postId),
  });

  const createPostMutation = useMutation({
    mutationFn: postPost,
    onSuccess: async (response, variables) => {
      if (response.status !== 201) return;

      !response.data.result.pointDifference
        ? toast.success(TOAST.POST.createNoPoints)
        : toast.success(TOAST.POST.create);

      const newPostId = response.data.result.postId;
      try {
        await createThumbnailMutation.mutateAsync({
          boardId: variables.boardId,
          postId: newPostId,
        });
      } catch (err) {
        toast.error('썸네일 생성 중 오류가 발생했습니다.');
      }

      queryClient.removeQueries(QUERY_KEY.post());
      invalidUserInfoQuery();

      const path = isNotice
        ? toNoticeDetail(boardKey, newPostId)
        : toDetail(boardKey, newPostId);
      navigate(path, { replace: true });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!boardId) {
      toast.info(TOAST.POST.selectBoard);
      return;
    }

    if (!title.trim()) {
      toast.info(TOAST.POST.emptyTitle);
      return;
    }
    if (!content.trim()) {
      toast.info(TOAST.POST.emptyContent);
      return;
    }

    if (submitLockRef.current) return;
    submitLockRef.current = true;

    createPostMutation.mutate(data, {
      onSettled: () => {
        submitLockRef.current = false;
      },
    });
  };

  return (
    <>
      {isGuideOpened && (
        <Guideline
          guideImages={[attachmentGuide1, attachmentGuide2]}
          guideStyle={{
            width: '27.4rem',
            height: '36.1rem',
            position: 'absolute',
            bottom: '10.2rem',
          }}
        >
          <div className={styles.buttons}>
            <label className={styles.guideButton1}>
              <CheckBox
                id='dontShowAgain'
                checked={dontShowAgain}
                onChange={() => setDontShowAgain((prev) => !prev)}
              />
              <p className={styles.guideButton1Text}>다시 보지 않기</p>
            </label>
            <button
              className={styles.guideButton2}
              onClick={() => {
                if (dontShowAgain) {
                  disableGuide();
                }
                closeGuide();
              }}
            >
              닫기
            </button>
          </div>
        </Guideline>
      )}

      <div className={styles.container}>
        {(createPostMutation.isPending ||
          createThumbnailMutation.isPending) && (
          <div className={styles.fetchLoadingContainer}>
            <FetchLoading>게시글 처리 중...</FetchLoading>
          </div>
        )}
        <div className={styles.main}>
          <div className={styles.top}>
            <CloseAppBar backgroundColor={'#eaf5fd'}>
              <ActionButton
                onClick={handleSubmit}
                disabled={
                  createPostMutation.isPending ||
                  createThumbnailMutation.isPending
                }
              >
                등록
              </ActionButton>
            </CloseAppBar>
          </div>
          <div className={styles.center}>
            <div className={styles.categorySelect}>
              <div className={styles.categorySelectContainer}>
                <IconMultiClipboardBlue width={21} height={22} />
                <p className={styles.categorySelectText}>
                  {!isGlobalNotice && isNotice
                    ? `${boardName} 공지`
                    : boardName}
                </p>
              </div>
            </div>

            <div className={styles.profileBox}>
              <div className={styles.profileBoxLeft}>
                {userInfo?.userRoleId !== ROLE.admin &&
                userInfo?.userRoleId !== ROLE.official ? (
                  <IconMultiCloudLogo
                    width={25}
                    height={16}
                    role='img'
                    aria-label='로고'
                  />
                ) : (
                  <Badge
                    userRoleId={userInfo?.userRoleId}
                    width={24}
                    height={24}
                  />
                )}
                <p>{userInfo?.nickname}</p>
                <p className={styles.dot}></p>
                <p>{DateTime.format(new Date(), 'MD_HM')}</p>
              </div>
            </div>

            <div className={styles.content}>
              <TextareaAutosize
                className={styles.title}
                placeholder='제목을 입력해주세요'
                value={title}
                onChange={handleTitleChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                  }
                }}
                onFocus={() => setIsTitleFocused(true)}
                onBlur={() => setIsTitleFocused(false)}
              />
              {/*<TextareaAutosize
                className={styles.text}
                placeholder='내용을 작성해주세요'
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />*/}
              <EditorContainer
                placeholder='내용'
                onEditorReady={(editorInstance) => setEditor(editorInstance)}
                onChangeEditor={(editor) => {
                  const sanitized = sanitizeHtml(
                    preserveEmptyParagraphs(editor.getHTML())
                  );
                  setContent(sanitized);
                }}
              />
              <AttachmentList
                attachmentsInfo={attachmentsInfo}
                setAttachmentsInfo={setAttachmentsInfo}
              />
            </div>
          </div>
        </div>

        <IllustrationTrashcanCircle
          width={21}
          height={22}
          className={`${isTrashOverlapped ? styles.trashVisible : styles.trashInvisible}`}
          onDragEnter={(e) => {
            e.preventDefault();
            setIsTrashOverlapped(true);
          }}
          onDragOver={(e) => {
            setIsTrashOverlapped(true);
            e.preventDefault();
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsTrashOverlapped(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            const draggedIndex = parseInt(
              e.dataTransfer.getData('text/plain'),
              10
            );
            setTrashImageIndex(draggedIndex);
            setIsTrashOverlapped(false);
            trashImageConfirmModal.openModal();
          }}
        />
        <AttachmentBar
          attachmentsInfo={attachmentsInfo}
          setAttachmentsInfo={setAttachmentsInfo}
          editor={editor}
          isTitleFocused={isTitleFocused}
        />
      </div>

      {trashImageConfirmModal.isOpen && (
        <ConfirmModal
          modalText={ATTACHMENT_MODAL_TEXT.DELETE_ATTACHMENT}
          onConfirm={() => {
            setAttachmentsInfo((prev) =>
              prev
                .slice(0, trashImageIndex)
                .concat(prev.slice(trashImageIndex + 1))
            );
            trashImageConfirmModal.closeModal();
          }}
          onCancel={() => {
            trashImageConfirmModal.closeModal();
          }}
        />
      )}

      {modal.id === 'exit-page' && (
        <ConfirmModal
          modalText={CONFIRM_MODAL_TEXT.EXIT_PAGE}
          onConfirm={handleExitPage}
        />
      )}
    </>
  );
}
